import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight, Check, Shield, Camera, Mic, Brain,
  CreditCard, Eye, Users, ArrowRight, Fingerprint,
  Target, Zap, Lock, BarChart3, LogIn,
  Flame, Trophy, Video, FileText, GraduationCap, Download, MapPin
} from 'lucide-react';
import { ValuePillars, OurApproach, AcademyFit } from './AcademyProgram';
import ProtocolTag from '../components/saa/ProtocolTag';

const GOLD = '#C9A961';
const ORANGE = '#FF5E00';
const PURPLE = '#7B2CBF';
const BG = '#050508';
const SURFACE = '#0A0A0E';

const Academy = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isParentView = searchParams.get('type') === 'parent';
  const TC = isParentView ? PURPLE : ORANGE;
  const RGB = isParentView ? '123,44,191' : '255,94,0';
  const [activeCell, setActiveCell] = useState(null);
  const [ct, setCt] = useState({ gpa: 0, std: 0, att: 0 });
  const [billing, setBilling] = useState('month'); // 'month' | 'year'
  const [checkingOut, setCheckingOut] = useState(null);

  const startCheckout = async (tierKey) => {
    const token = localStorage.getItem('academy_token');
    if (!token) { navigate('/academy/login'); return; }
    setCheckingOut(tierKey);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const me = await fetch(`${base}/api/academy/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()).catch(() => null);
      const grade = (me?.user || me)?.grade_level || null;
      const res = await fetch(`${base}/api/academy/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tier: tierKey, billing_interval: billing, grade_level: grade ? Number(grade) : null }),
      }).then((r) => r.json());
      if (res.checkout_url) { window.location.href = res.checkout_url; }
      else { navigate('/academy/enroll'); }
    } catch { navigate('/academy/enroll'); }
    finally { setCheckingOut(null); }
  };

  useEffect(() => {
    const T = { gpa: 3.42, std: 95, att: 98.7 };
    let s = 0;
    const iv = setInterval(() => {
      s++;
      const p = s / 60;
      setCt({ gpa: +(T.gpa * Math.min(p, 1)).toFixed(2), std: Math.round(T.std * Math.min(p, 1)), att: +(T.att * Math.min(p, 1)).toFixed(1) });
      if (s >= 60) clearInterval(iv);
    }, 33);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: 'transparent' }} data-testid="academy-landing">

      {/* ============ HERO ============ */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="academy-hero">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-25"
          poster="https://images.unsplash.com/photo-1461896836934-bd45ba8fcf9b?w=1920&q=80">
          <source src="https://videos.pexels.com/video-files/4557781/4557781-uhd_3840_2160_30fps.mp4" type="video/mp4" />
        </video>

        {/* Student Login — pinned inside hero header, brutalist border, no clash with global nav */}
        <button
          onClick={() => navigate('/academy/login')}
          className="absolute top-20 right-4 lg:right-8 z-30 group flex items-center gap-2 border-2 border-white/30 hover:border-white bg-black/60 hover:bg-black backdrop-blur-md px-4 py-2.5 transition-all duration-300"
          style={{ borderRadius: 0 }}
          data-testid="academy-student-login-btn"
          aria-label="Student Login"
        >
          <LogIn className="w-3.5 h-3.5 text-white/80 group-hover:text-white transition-colors" />
          <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/90 group-hover:text-white transition-colors">
            Student Login
          </span>
        </button>

        {/* Grid */}
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(${RGB},0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(${RGB},0.2) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(45deg, rgba(${RGB},0.08) 1px, transparent 1px), linear-gradient(-45deg, rgba(${RGB},0.08) 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 30%, transparent 0%, ${BG}60 40%, ${BG} 75%)` }} />
        <div className="absolute inset-x-0 bottom-0 h-64" style={{ background: `linear-gradient(to top, ${BG}, transparent)` }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-16 w-full">
          {/* Logo */}
          <div className="text-center pt-20 mb-4">
            <img src={isParentView ? '/images/scholar-parent-logo-new.png' : '/images/scholar-athlete-logo-new.png'}
              alt="Scholar Athlete Academy" className="mx-auto h-[200px] md:h-[280px] lg:h-[360px] w-auto"
              style={{ filter: `drop-shadow(0 0 60px rgba(${RGB},0.35))` }} />
          </div>

          {/* Sovereign Badge */}
          <div className="text-center mb-4">
            <ProtocolTag number="001" label={isParentView ? 'Executive Mirror' : 'Sovereign Academy'} color={TC} />
          </div>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 border border-white/10 backdrop-blur-xl px-5 py-2.5">
              <Shield className="w-4 h-4" style={{ color: GOLD }} />
              <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>The Sovereign Academy</span>
              <span className="h-3 w-px bg-white/20" />
              <span className="text-[10px] font-bold tracking-[0.25em] uppercase text-gray-400">Accredited Intelligence</span>
            </div>
          </div>

          {/* Headline */}
          <div className="max-w-5xl mx-auto text-center mb-6">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-black uppercase leading-[0.9] tracking-[-0.03em]">
              A Better
              <br />
              <span style={{ color: ORANGE }}>Academy</span> for
              <br />
              <span style={{ color: ORANGE }}>Scholar Athletes</span>
            </h1>
            <p className="text-sm md:text-base font-bold tracking-tight mt-5 max-w-3xl mx-auto" style={{ color: GOLD }}>
              A Mastery-Aligned, Guided Independent Study Academy Engineered for Modern Scholar-Athletes.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              <div className="inline-flex items-center gap-1.5 border border-white/10 px-2.5 py-1">
                <Shield className="w-3 h-3" style={{ color: GOLD }} />
                <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-gray-400">California State Standards Aligned</span>
              </div>
              <div className="inline-flex items-center gap-1.5 border border-white/10 px-2.5 py-1" data-testid="home-compliance-badge">
                <Shield className="w-3 h-3" style={{ color: GOLD }} />
                <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-gray-400">Compliant In Every State We Operate</span>
              </div>
            </div>
          </div>

          <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto text-center leading-relaxed mb-10">
            {isParentView
              ? 'A seamless K-12 professional path with total parental visibility. Biometric verification. State of California Standards. One sovereign system.'
              : 'A seamless K-12 professional path where every lesson connects to the field. Biometric accountability. Cognitive mastery. State of California Standards Engine.'}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button onClick={() => navigate('/solo')}
              className="inline-flex items-center justify-center gap-2 font-black text-sm uppercase tracking-[0.15em] px-10 py-5 transition-all hover:-translate-y-0.5 hover:shadow-lg bg-orange-500 hover:bg-orange-400 text-black"
              style={{ clipPath: 'polygon(0 0, 100% 0, 96% 100%, 4% 100%)' }}
              data-testid="hero-solo-btn">
              🏫 Enroll in SAA SOLO · K-8 <ChevronRight className="w-5 h-5" />
            </button>
            <button onClick={() => navigate('/academy/enroll')}
              className="inline-flex items-center justify-center gap-2 font-black text-sm uppercase tracking-[0.15em] px-10 py-5 border-2 transition-all hover:-translate-y-0.5"
              style={{ borderColor: `${TC}80`, color: TC, clipPath: 'polygon(0 0, 100% 0, 96% 100%, 4% 100%)' }}
              data-testid="hero-primary-btn">
              Reserve Your Seat · High School <ChevronRight className="w-5 h-5" />
            </button>
            <button onClick={() => navigate('/academy/login')}
              className="inline-flex items-center justify-center gap-2 font-black text-sm uppercase tracking-[0.15em] px-10 py-5 border-2 transition-all hover:-translate-y-0.5"
              style={{ borderColor: `${GOLD}50`, color: GOLD, clipPath: 'polygon(0 0, 100% 0, 96% 100%, 4% 100%)' }}
              data-testid="hero-secondary-btn">
              <Lock className="w-4 h-4" /> Member Login
            </button>
          </div>

          {/* SOLO teaser strip */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10 text-[10px] uppercase tracking-[0.25em] font-black">
            <span className="text-orange-500">NEW · SAA SOLO</span>
            <span className="text-gray-600">·</span>
            <span className="text-gray-400">Grades K-8 · Track A $399/mo · Track B $299/mo</span>
            <span className="text-gray-600">·</span>
            <button onClick={() => navigate('/solo/preview')} className="text-orange-400 hover:text-orange-300 underline underline-offset-2" data-testid="solo-preview-link">
              Try a Sample Day →
            </button>
          </div>

          {/* Trust Line */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-[10px] text-gray-600 uppercase tracking-[0.25em] font-bold">
            <span>CA State Standards Aligned</span>
            <span className="h-2 w-px bg-white/10" />
            <span>Biometric Verified</span>
            <span className="h-2 w-px bg-white/10" />
            <span>Sovereign System</span>
            <span className="h-2 w-px bg-white/10" />
            <span>K-12 Professional Path</span>
          </div>
        </div>

        {/* Corner Stats */}
        <div className="absolute bottom-0 right-0 z-10 hidden lg:flex gap-px">
          {[
            { l: 'Core GPA', v: ct.gpa.toFixed(2), s: 'Honors Threshold: 3.5' },
            { l: 'Standards', v: `${ct.std}%`, s: 'CA State Aligned' },
            { l: 'Attendance', v: `${ct.att}%`, s: 'Biometric Verified' }
          ].map((d, i) => (
            <div key={i} className="px-7 py-4 backdrop-blur-xl" style={{ backgroundColor: 'rgba(10,10,14,0.9)', borderTop: `1px solid ${GOLD}30` }}>
              <div className="text-[8px] uppercase tracking-[0.3em] font-bold mb-1" style={{ color: GOLD }}>{d.l}</div>
              <div className="text-xl font-black text-white font-mono">{d.v}</div>
              <div className="text-[8px] text-gray-600">{d.s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ OPERATIONAL VALUE PILLARS (LEAD) ============ */}
      <ValuePillars TC={TC} RGB={RGB} BG={BG} SURFACE={SURFACE} />

      {/* ============ POWER PILLARS OF SOVEREIGNTY (BRAND PHILOSOPHY) ============ */}
      <section className="relative py-28 px-6 lg:px-8" data-testid="power-pillars">
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(${RGB},0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(${RGB},0.05) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(${BG}, transparent 15%, transparent 85%, ${BG})` }} />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px w-16" style={{ backgroundColor: GOLD }} />
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>The Philosophy Beneath The Model</span>
          </div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight mb-4">Sovereignty Is<br/><span className="text-gray-600">Not Optional.</span></h2>
          <p className="text-gray-500 text-sm max-w-xl mb-16">Three non-negotiable systems that govern every interaction inside the Academy. No shortcuts. No overrides. No exceptions.</p>

          {/* 3 Pillars */}
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.04]">
            {[
              {
                id: 'biometric', icon: Eye, num: '01', name: 'Biometric Accountability', system: 'On-Device · MediaPipe',
                body: 'Every session begins with on-device facial verification. The system confirms identity with 98%+ confidence before a single lesson loads — and no image data ever leaves the device. No proxy attendance. No gaming the system. Parents receive timestamped Proof of Presence with biometric confidence scores — because accountability is non-negotiable.',
                stat: '98.0%', statLabel: 'Verification Confidence'
              },
              {
                id: 'cognitive', icon: Brain, num: '02', name: 'Cognitive Mastery', system: 'Reasoning Gate',
                body: 'Every module requires the student to demonstrate WHY and HOW a concept works before the assessment unlocks. No multiple-choice guessing. No rote memorization. Open-response reasoning evaluated by AI that rewards athletic connections and penalizes surface-level thinking. This is how scholars are forged.',
                stat: '100%', statLabel: 'Logic Gated'
              },
              {
                id: 'state', icon: Shield, num: '03', name: 'State of CA Compliance', system: 'Standards Engine',
                body: 'Every quiz, every grade, every standard mastered — mapped in real-time to the California State Standards framework. Mastery is logged per standard. Reports are export-ready for parents, transcripts, and state record-keeping. The system enforces the academic hierarchy by design.',
                stat: 'CA STATE', statLabel: 'Auto-Tracked'
              }
            ].map((pillar) => (
              <div key={pillar.id} className="p-8 lg:p-10 group" style={{ backgroundColor: SURFACE }} data-testid={`pillar-${pillar.id}`}>
                <div className="flex items-start justify-between mb-8">
                  <div className="text-[9px] font-bold tracking-[0.3em] uppercase text-gray-700">{pillar.system}</div>
                  <div className="text-right">
                    <div className="text-2xl font-black font-mono text-white">{pillar.stat}</div>
                    <div className="text-[8px] text-gray-600 uppercase tracking-wider">{pillar.statLabel}</div>
                  </div>
                </div>
                <div className="text-[60px] font-black leading-none mb-4" style={{ color: `${TC}15` }}>{pillar.num}</div>
                <div className="text-xs font-black tracking-[0.2em] uppercase mb-2" style={{ color: TC }}>{pillar.name}</div>
                <p className="text-gray-400 text-sm leading-[1.8]">{pillar.body}</p>
                <div className="w-8 h-8 flex items-center justify-center mt-6 border border-white/10 group-hover:border-white/30 transition-all">
                  <pillar.icon className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ OUR APPROACH ============ */}
      <OurApproach TC={TC} RGB={RGB} BG={BG} SURFACE={SURFACE} />

      {/* ============ INSIDE THE COCKPIT (3-Column Preview) ============ */}
      <section className="py-28 px-6 lg:px-8" style={{ backgroundColor: SURFACE }} data-testid="cockpit-preview">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px w-16" style={{ backgroundColor: GOLD }} />
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>Inside The IQ</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4">What The Student Sees.</h2>
          <p className="text-gray-500 text-sm max-w-lg mb-12">The Three-Column Intelligence Layout. Every lesson is a strategy session — not a textbook page.</p>

          {/* 3 Column Mockup */}
          <div className="border border-white/10 overflow-hidden" style={{ backgroundColor: BG }}>
            {/* Top bar mock */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
              <div className="w-3 h-3 rounded-full bg-red-500/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
              <div className="w-3 h-3 rounded-full bg-green-500/60" />
              <span className="text-[9px] text-gray-600 ml-4 font-mono">scholarathlete.academy/lesson</span>
            </div>
            <div className="grid grid-cols-12 min-h-[320px]">
              {/* Left: Strategy Room */}
              <div className="col-span-3 p-5 border-r border-white/5">
                <div className="text-[8px] font-bold tracking-[0.3em] uppercase mb-4" style={{ color: GOLD }}>Strategy Room</div>
                <div className="space-y-3">
                  <div className="p-2 border border-white/5" style={{ backgroundColor: SURFACE }}>
                    <div className="text-[8px] text-gray-600">Standard</div>
                    <div className="text-white font-bold text-xs font-mono">8.EE.7</div>
                    <div className="text-[8px] text-gray-500">Linear Equations</div>
                  </div>
                  <div className="p-2 border border-white/5" style={{ backgroundColor: SURFACE }}>
                    <div className="text-[8px] text-gray-600">CA Standard</div>
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-green-500/20 text-green-400">ON TRACK</span>
                  </div>
                  <div>
                    <div className="text-[8px] text-gray-600 mb-1">Vocabulary</div>
                    {['Linear Equation', 'Inverse Operations', 'Distributive Property'].map((t, i) => (
                      <div key={i} className="text-[9px] text-gray-500 py-1 border-b border-white/5">{t}</div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Center: Main Court */}
              <div className="col-span-6 p-5">
                <div className="text-[8px] font-bold tracking-[0.3em] uppercase mb-3" style={{ color: GOLD }}>The Game Plan</div>
                <div className="space-y-2">
                  <div className="h-2 bg-white/10 w-full" />
                  <div className="h-2 bg-white/10 w-11/12" />
                  <div className="h-2 bg-white/10 w-full" />
                  <div className="h-2 bg-white/10 w-9/12" />
                </div>
                <div className="mt-4 p-3 border-l-2" style={{ borderColor: GOLD, backgroundColor: `${GOLD}08` }}>
                  <div className="text-[8px] font-bold uppercase mb-1" style={{ color: GOLD }}>Sports Logic</div>
                  <div className="h-2 bg-white/10 w-10/12 mb-1" />
                  <div className="h-2 bg-white/10 w-full mb-1" />
                  <div className="h-2 bg-white/10 w-8/12" />
                </div>
                <div className="mt-4 p-3 border-2" style={{ borderColor: `${TC}40` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-3 h-3" style={{ color: TC }} />
                    <span className="text-[9px] font-bold uppercase" style={{ color: TC }}>Reasoning Gate</span>
                  </div>
                  <div className="h-8 bg-white/5 border border-white/10" />
                </div>
              </div>
              {/* Right: Training Kit */}
              <div className="col-span-3 p-5 border-l border-white/5">
                <div className="text-[8px] font-bold tracking-[0.3em] uppercase mb-4" style={{ color: GOLD }}>Training Kit</div>
                <div className="p-3 border border-white/10 mb-3 text-center" style={{ backgroundColor: SURFACE }}>
                  <Mic className="w-4 h-4 mx-auto mb-1" style={{ color: GOLD }} />
                  <div className="text-[9px] text-gray-500">Listen to Coach</div>
                </div>
                <div className="p-3 border border-white/10 mb-3 text-center bg-white">
                  <div className="text-[9px] text-black font-bold">Download PDF</div>
                </div>
                <div className="space-y-2 mt-4">
                  <div className="text-[8px] text-gray-600">Progress</div>
                  {['Game Plan', 'Sports Logic', 'Reasoning Gate', 'Assessment'].map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 ${i < 2 ? 'bg-green-500' : 'border border-gray-700'}`} />
                      <span className="text-[9px] text-gray-500">{s}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PARENT COMMAND CENTER ============ */}
      <section className="relative py-28 px-6 lg:px-8" data-testid="parent-section">
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(${RGB},0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(${RGB},0.04) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="h-px w-16" style={{ backgroundColor: GOLD }} />
              <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>For The Performance Parent</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">Total Visibility.</h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              The Parent Command Center is not a report card. It's a live intelligence feed into your child's
              academic performance, attendance verification, standards compliance, and CIF / high-school eligibility trajectory.
              Real-time. Biometric-verified. Uncompromising.
            </p>
            <div className="space-y-4 mb-10">
              {[
                { icon: Fingerprint, label: 'Proof of Presence', desc: 'Timestamped biometric logs with AI confidence scores' },
                { icon: BarChart3, label: 'Accreditation Readiness', desc: 'CA Standards mastery meter — per subject, per week' },
                { icon: Shield, label: 'CIF Eligibility Mirror', desc: 'Core GPA, D1/D2 status, automatic red flag alerts' },
                { icon: CreditCard, label: 'Financial Office', desc: 'Tuition management, payment history, enrollment status' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center border border-white/10" style={{ backgroundColor: `${GOLD}08` }}>
                    <item.icon className="w-4 h-4" style={{ color: GOLD }} />
                  </div>
                  <div>
                    <div className="text-white font-bold text-sm">{item.label}</div>
                    <div className="text-gray-500 text-xs">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => navigate('/academy/parent')}
              className="inline-flex items-center gap-2 font-black text-sm uppercase tracking-[0.15em] px-10 py-5 transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: GOLD, color: '#000', clipPath: 'polygon(0 0, 100% 0, 96% 100%, 4% 100%)' }}
              data-testid="parent-cta">
              Access Command Center <ArrowRight className="w-5 h-5" />
            </button>
          </div>

          {/* Mockup */}
          <div className="border border-white/10 p-6 lg:p-8" style={{ backgroundColor: BG }}>
            <div className="flex items-center gap-3 mb-6">
              <img src="/images/scholar-athlete-logo-new.png" alt="" className="h-6 w-auto opacity-50" />
              <span className="text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>Live Dashboard</span>
            </div>
            <div className="border border-white/5 p-4 mb-4" style={{ backgroundColor: SURFACE }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Proof of Presence</span>
                <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-green-400 text-[10px] font-bold">VERIFIED</span></div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[{ v: '8:02 AM', l: 'Check-in' }, { v: '98.0%', l: 'Confidence' }, { v: 'EyePop', l: 'Provider' }].map((d, i) => (
                  <div key={i}><div className="text-white font-black text-lg font-mono">{d.v}</div><div className="text-[9px] text-gray-600">{d.l}</div></div>
                ))}
              </div>
            </div>
            <div className="border border-white/5 p-4" style={{ backgroundColor: SURFACE }}>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-3">Accreditation Readiness</div>
              {[
                { n: 'Mathematics', p: 88, c: '#3B82F6' },
                { n: 'English Language Arts', p: 100, c: '#8B5CF6' },
                { n: 'Science', p: 100, c: '#10B981' },
                { n: 'Social Studies', p: 67, c: '#F59E0B' }
              ].map((s, i) => (
                <div key={i} className="mb-2.5">
                  <div className="flex justify-between text-[10px] mb-1"><span className="text-gray-400">{s.n}</span><span className="font-bold font-mono" style={{ color: s.c }}>{s.p}%</span></div>
                  <div className="h-1 bg-gray-800"><div className="h-full" style={{ width: `${s.p}%`, backgroundColor: s.c }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>

      {/* ============ FEATURES & BENEFITS ============ */}
      <section className="relative py-28 px-6 lg:px-8" data-testid="features-benefits">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px w-16" style={{ backgroundColor: GOLD }} />
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>The Full Playbook</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4">Every Feature. Every Benefit.</h2>
              <p className="text-gray-500 text-sm max-w-xl">A complete learning system engineered for the student-athlete — daily reps, live mastery, and total transparency for parents. Here's what your family gets.</p>
            </div>
            <a
              href="/downloads/saa-features-benefits.pdf" target="_blank" rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-black text-[11px] uppercase tracking-[0.2em] text-black rounded-sm transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: GOLD }} data-testid="download-features-pdf"
            >
              <Download className="w-4 h-4" /> Download Features Sheet
            </a>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Flame, color: '#FF5E00', title: 'The Combine', tag: 'GameDay Skill Engine', benefit: 'Endless, AI-generated sports practice that adapts to weak spots — like IXL, but every rep is a game scenario.' },
              { icon: FileText, color: '#3B82F6', title: 'Daily AI Worksheets', tag: 'Standards-Aligned', benefit: 'A fresh, auto-graded worksheet every school day with instant written feedback on open responses.' },
              { icon: BarChart3, color: '#10B981', title: 'Mastery Radar', tag: 'Live Progress', benefit: 'A 6-point skill map + Overall Scholar Rating so strengths and gaps are visible at a glance.' },
              { icon: Trophy, color: '#C9A961', title: 'Scholar Dollars, Streaks & Badges', tag: 'Motivation Engine', benefit: 'Daily quests, level-ups, and "on fire" streaks turn showing up every day into a game they want to win.' },
              { icon: GraduationCap, color: '#8B5CF6', title: 'Scholar Portfolio', tag: 'Verifiable Résumé', benefit: 'A shareable, verified record of mastery, badges, and credits for coaches, recruiters, and schools.' },
              { icon: Video, color: '#EF4444', title: 'Live Classroom', tag: 'Face-to-Face', benefit: 'Real-time video sessions a coach can target to a grade or a single scholar — help exactly when it counts.' },
              { icon: Users, color: '#06B6D4', title: 'Family Hub', tag: 'Parent Transparency', benefit: 'A passwordless magic link shows parents their child\u2019s streaks, mastery, and materials in 10 seconds.' },
              { icon: MapPin, color: '#F59E0B', title: 'Multi-State Standards', tag: 'CA \u00b7 FL \u00b7 TX', benefit: 'Pick your state at signup and the daily rhythm anchors to California, Florida B.E.S.T., or Texas TEKS.' },
            ].map((f, i) => (
              <div
                key={i}
                className="group relative p-6 border border-white/10 rounded-sm overflow-hidden transition-colors hover:border-white/25"
                style={{ backgroundColor: SURFACE }}
                data-testid={`feature-card-${i}`}
              >
                <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-20 transition-opacity group-hover:opacity-40" style={{ backgroundColor: f.color }} />
                <div className="w-11 h-11 flex items-center justify-center rounded-sm mb-5" style={{ backgroundColor: `${f.color}1A`, border: `1px solid ${f.color}55` }}>
                  <f.icon className="w-5 h-5" style={{ color: f.color }} />
                </div>
                <div className="text-[9px] font-bold uppercase tracking-[0.25em] mb-1.5" style={{ color: f.color }}>{f.tag}</div>
                <h3 className="text-lg font-black tracking-tight mb-2">{f.title}</h3>
                <p className="text-[13px] leading-relaxed text-gray-500">{f.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      </section>

      {/* ============ WHY CHOOSE / RIGHT FIT / ENROLLMENT ============ */}
      <AcademyFit TC={TC} RGB={RGB} BG={BG} SURFACE={SURFACE} navigate={navigate} />

      {/* ============ TUITION ============ */}
      <section id="tuition" className="py-28 px-6 lg:px-8" style={{ backgroundColor: SURFACE }} data-testid="tuition-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <img src="/images/scholar-athlete-logo-new.png" alt="" className="mx-auto h-14 w-auto opacity-30 mb-6" />
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-12" style={{ backgroundColor: GOLD }} />
              <span className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>Enrollment</span>
              <div className="h-px w-12" style={{ backgroundColor: GOLD }} />
            </div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight">Secure Your Seat.</h2>

            {/* Monthly / Annual toggle */}
            <div className="inline-flex mt-8 border border-white/15 rounded-full p-1" data-testid="billing-toggle">
              <button onClick={() => setBilling('month')} className="px-5 py-2 text-xs font-black uppercase tracking-[0.15em] rounded-full transition-all" style={{ backgroundColor: billing === 'month' ? TC : 'transparent', color: billing === 'month' ? '#000' : '#9ca3af' }} data-testid="billing-month">Monthly</button>
              <button onClick={() => setBilling('year')} className="px-5 py-2 text-xs font-black uppercase tracking-[0.15em] rounded-full transition-all" style={{ backgroundColor: billing === 'year' ? TC : 'transparent', color: billing === 'year' ? '#000' : '#9ca3af' }} data-testid="billing-year">Annual <span className="opacity-70">· Save</span></button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/5">
            {[
              { key: 'gameday_skill_engine', n: 'GAMEDAY SKILL ENGINE', s: 'Full Access to The Combine', pm: '$19.99', py: '$179', tag: 'Full access to The Combine — unlimited AI-powered, sports-based practice across Math, ELA, Science & Social Studies', funding: false, f: ['Full access to The Combine — our sports-centric IXL: unlimited AI practice that never runs out', 'Grade-aligned to CA standards (Grades 4-9), all 4 core subjects', 'Train your way: All-Around, single subject, Today\u2019s Lesson, or Target-a-Weakness', 'Gamified streaks, on-fire hot streaks, coach hints & Scholar Dollars', 'Feeds your Mastery Radar, Scholar Badges & Daily Quests'], cta: 'Start Skill Engine', hl: false },
              { key: 'virtual_private_school', n: 'FULL VIRTUAL PRIVATE SCHOOL', s: 'Complete Enrollment', pm: '$299', py: '$2,500', tag: null, funding: false, f: ['Complete state-registered curriculum', '3-Hour Daily Station Rotation Matrix', 'AI Socratic Tutor', 'Automated Grading', 'Official Transcript Generation', 'Full access to The Combine (unlimited sports-based practice)'], cta: 'Enroll Full-Time', hl: true },
              { key: 'live_classroom_hybrid', n: 'LIVE CLASSROOM HYBRID STREAM', s: 'Hybrid + Replay Vault', pm: '$499', py: '$5,000', tag: null, funding: false, f: ['Everything in Full Virtual Private School', 'Daily Video Replay Vault (classroom streams)', 'Station 1 Direct Instruction Recordings', 'Priority Teacher Support'], cta: 'Join Hybrid Stream', hl: false },
            ].map((t, i) => (
              <div key={i} className="p-8 lg:p-10 relative flex flex-col" style={{ backgroundColor: BG, borderTop: t.hl ? `2px solid ${TC}` : '2px solid transparent' }} data-testid={`tier-card-${t.key}`}>
                {t.hl && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2"><span className="text-[9px] font-bold tracking-[0.2em] uppercase px-4 py-1.5" style={{ backgroundColor: TC, color: '#000' }}>Recommended</span></div>}
                <div className="text-[9px] text-gray-600 uppercase tracking-wider mb-1">{t.s}</div>
                <h3 className="text-base font-black text-white tracking-wider mb-3 leading-tight">{t.n}</h3>
                {t.funding && <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 self-start" style={{ backgroundColor: 'rgba(201,169,97,0.12)', border: `1px solid ${GOLD}` }} data-testid={`funding-badge-${t.key}`}><Shield className="w-3 h-3" style={{ color: GOLD }} /><span className="text-[9px] font-black tracking-wider uppercase" style={{ color: GOLD }}>State / Charter Funding Eligible</span></div>}
                <div className="flex items-baseline gap-1 mb-2"><span className="text-4xl font-black text-white font-mono" data-testid={`price-${t.key}`}>{billing === 'year' ? t.py : t.pm}</span><span className="text-gray-600 text-sm">{billing === 'year' ? '/yr' : '/mo'}</span></div>
                {t.tag && <div className="mb-5 px-3 py-2 text-[11px] font-bold leading-snug" style={{ backgroundColor: `rgba(${RGB},0.1)`, color: TC }} data-testid={`tag-${t.key}`}>{t.tag}</div>}
                <ul className="space-y-3 mb-8 flex-1">
                  {t.f.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2.5"><Check className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: t.hl ? TC : GOLD }} /><span className="text-gray-400 text-sm">{f}</span></li>
                  ))}
                </ul>
                <button onClick={() => startCheckout(t.key)} disabled={checkingOut === t.key} className="w-full font-black text-sm uppercase tracking-[0.15em] py-4 transition-all hover:-translate-y-0.5 disabled:opacity-60"
                  style={{ backgroundColor: t.hl ? TC : 'transparent', color: t.hl ? '#000' : 'white', border: t.hl ? 'none' : '1px solid rgba(255,255,255,0.15)', clipPath: 'polygon(0 0, 100% 0, 97% 100%, 3% 100%)' }}
                  data-testid={`tier-cta-${t.key}`}>
                  {checkingOut === t.key ? 'Loading…' : t.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-28 px-6 lg:px-8" data-testid="final-cta">
        <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(rgba(${RGB},0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(${RGB},0.05) 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
        <div className="relative z-10 max-w-4xl mx-auto">
          <img src="/images/scholar-athlete-logo-new.png" alt="" className="h-16 w-auto opacity-25 mb-8" />
          <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-6">Education.<br/><span style={{ color: TC }}>Engineered.</span></h2>
          <p className="text-gray-500 text-base max-w-lg mb-10">Every click logged against state standards. Every lesson connected to the field. Every student tracked toward eligibility. This is the Sovereign Academy.</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={() => navigate('/academy/enroll')}
              className="inline-flex items-center justify-center gap-2 font-black text-sm uppercase tracking-[0.15em] px-12 py-5 transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: TC, color: '#000', clipPath: 'polygon(0 0, 100% 0, 96% 100%, 4% 100%)' }}
              data-testid="final-apply">
              Reserve Your Seat
            </button>
            <button onClick={() => navigate('/academy/login')}
              className="inline-flex items-center justify-center gap-2 font-black text-sm uppercase tracking-[0.15em] px-12 py-5 border border-white/15 text-gray-400 hover:text-white hover:border-white/30 transition-all"
              data-testid="final-login">
              Member Login
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Academy;
